import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

axios.interceptors.response.use(
  response => response,
  error => {
    if (error.response && error.response.status === 401) {
      if (window.location.pathname.startsWith('/yonetim')) {
        localStorage.removeItem('admin_token');
        window.location.href = '/yonetim';
      } else {
        localStorage.removeItem('crm_token');
        localStorage.removeItem('crm_user');
        localStorage.removeItem('crm_industry');
        window.location.href = '/login';
      }
    }
    // Abonelik ödemesi gecikip yazma erişimi kısıtlanmış (bkz. server/middleware/auth.js).
    // Tek tek her sayfanın kendi hata mesajını yazmasına gerek kalmasın diye burada
    // tek noktadan tutarlı bir uyarı gösteriliyor — sayfa hâlâ kendi hata state'ini
    // yönetebilir, bu sadece ek bir görünür bildirim.
    if (error.response && error.response.status === 402 && error.response.data?.error === 'SUBSCRIPTION_SUSPENDED') {
      toast.error(error.response.data.message || 'Ödemeniz gecikti, bu işlem yapılamıyor.');
    }
    // Şube panelde oturum açıkken pasife alınırsa (bkz. server/middleware/auth.js)
    // bir sonraki istekte buradan yakalanıp oturum sonlandırılır — sadece /login'i
    // bloklamak, o an açık oturumu olan personeli etkilemezdi.
    // Firma bu alan adında yok (başka markaya ait, bkz. server/utils/brands.js) - açık
    // kalmış oturumu kapatıp bu alan adının giriş sayfasına gönder; doğru adres söylenmez.
    if (error.response && error.response.status === 403 && error.response.data?.error === 'BRANCH_NOT_FOUND') {
      localStorage.removeItem('crm_token');
      localStorage.removeItem('crm_user');
      localStorage.removeItem('crm_industry');
      toast.error(error.response.data.message || 'Firma bulunamadı');
      window.location.href = '/login';
    }
    if (error.response && error.response.status === 403 && error.response.data?.error === 'BRANCH_INACTIVE') {
      localStorage.removeItem('crm_token');
      localStorage.removeItem('crm_user');
      localStorage.removeItem('crm_industry');
      toast.error(error.response.data.message || 'Firmanız Pasif Durumdadır.');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Login from './pages/Login';
import Register from './pages/Register';
import AdminLogin from './pages/AdminLogin';
import AdminPanel from './pages/AdminPanel';
import SetupWizard from './pages/SetupWizard';
import Dashboard from './pages/Dashboard';
import Calendar from './pages/Calendar';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import Opportunities from './pages/Opportunities';
import Staff from './pages/Staff';
import Services from './pages/Services';
import POS from './pages/POS';
import Products from './pages/Products';
import Settings from './pages/Settings';
import MetaAds from './pages/MetaAds';
import Integrations from './pages/Integrations';
import PreConsultations from './pages/PreConsultations';
import Measurements from './pages/Measurements';
import FittingSchedule from './pages/FittingSchedule';
import RentalContracts from './pages/RentalContracts';
import Surveys from './pages/Surveys';
import Quotes from './pages/Quotes';
import Production from './pages/Production';
import WeddingReservations from './pages/WeddingReservations';
import WeddingPackages from './pages/WeddingPackages';
import WeddingEventChecklist from './pages/WeddingEventChecklist';
import PatientTracking from './pages/PatientTracking';
import HotelReservations from './pages/HotelReservations';
import DentalTreatments from './pages/DentalTreatments';
import PatientSatisfaction from './pages/PatientSatisfaction';
import AutoReminders from './pages/AutoReminders';
import EcommerceOrders from './pages/EcommerceOrders';
import ReturnManagement from './pages/ReturnManagement';
import LeadsKanban from './pages/LeadsKanban';
import FinanceDashboard from './pages/FinanceDashboard';
import InventoryAlerts from './pages/InventoryAlerts';
import Automations from './pages/Automations';
import Messages from './pages/Messages';
import Campaigns from './pages/Campaigns';
import Reports from './pages/Reports';
import Calls from './pages/Calls';
import Announcements from './pages/Announcements';
import AnnouncementPopup from './components/AnnouncementPopup';
import Callbacks from './pages/Callbacks';
import Notifications from './pages/Notifications';
import Sidebar from './components/Sidebar';
import { fetchSiteSettings } from './hooks/useSiteSettings';
import PublicCheckout from './pages/PublicCheckout';
import PublicOnboarding from './pages/PublicOnboarding';
import Training from './pages/Training';
import Departments from './pages/Departments';

export const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [industry, setIndustry] = useState(null);
  const [showSetup, setShowSetup] = useState(false);
  const [industryModules, setIndustryModules] = useState({});
  const [modulesLoaded, setModulesLoaded] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);

  useEffect(() => {
    const savedUser = localStorage.getItem('crm_user');
    const token = localStorage.getItem('crm_token');
    const savedIndustry = localStorage.getItem('crm_industry');

    if (savedUser && token) {
      setUser(JSON.parse(savedUser));
      setIndustry(savedIndustry || 'beauty_salon');
      if (!savedIndustry) {
        setShowSetup(true);
      } else {
        fetchIndustryModules(savedIndustry);
      }
      fetchSubscriptionStatus();
    }
    setLoading(false);
    fetchSiteSettings().then(s => {
      if (s?.site_name) document.title = s.site_name;
    });
  }, []);

  const fetchIndustryModules = async (ind) => {
    try {
      const res = await fetch(`/api/auth/industry-modules/${ind}`);
      if (res.ok) {
        const modules = await res.json();
        setIndustryModules(modules);
      }
    } catch (err) {
      console.error(err);
    } finally {
      // industryModules yuklenene kadar (isModuleEnabled hep false donerken) modul-gated
      // route'lar HENUZ REGISTER OLMAMIS oluyor - sayfa yenilenince mevcut URL bu kisa
      // sure icinde hicbir Route'a eslesmeyip alttaki catch-all'a dusup anasayfaya
      // atiyordu (kullanicinin bulundugu sayfayi "unutmasi" budandi). Once/sonra
      // fark etmeksizin (basarili/basarisiz) yukleme bittigini isaretle ki asagida
      // Routes render edilmeden once bu tamamlanmis olsun.
      setModulesLoaded(true);
    }
  };

  // Sadece kalıcı banner'ı göstermek/gizlemek için — asıl yazma engeli
  // server/middleware/auth.js'te uygulanıyor, burası salt bilgilendirme.
  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('crm_token');
      if (!token) return;
      const res = await fetch('/api/branches/subscription-status', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) return;
      const data = await res.json();
      setSubscriptionStatus(data.status);
    } catch (err) { console.error(err); }
  };

  const isModuleEnabled = (module) => industryModules[module] === true;

  const login = (userData, token, userIndustry) => {
    const ind = userIndustry || 'beauty_salon';
    localStorage.setItem('crm_token', token);
    localStorage.setItem('crm_user', JSON.stringify(userData));
    localStorage.setItem('crm_industry', ind);
    setUser(userData);
    setIndustry(ind);
    fetchIndustryModules(ind);
    fetchSubscriptionStatus();
  };

  const logout = () => {
    localStorage.removeItem('crm_token');
    localStorage.removeItem('crm_user');
    localStorage.removeItem('crm_industry');
    setUser(null);
    setIndustry(null);
    setShowSetup(false);
    setSubscriptionStatus(null);
  };

  const handleSetupComplete = (selectedIndustry) => {
    localStorage.setItem('crm_industry', selectedIndustry);
    setIndustry(selectedIndustry);
    fetchIndustryModules(selectedIndustry);
    setShowSetup(false);
  };

  if (loading) {
    return <div className="loading">Yükleniyor...</div>;
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, industry, industryModules, subscriptionStatus }}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/kayit-ol" element={<Register />} />
          <Route path="/yonetim" element={<AdminLogin />} />
          <Route path="/yonetim/panel" element={<AdminPanel />} />
          <Route path="/pay/:id/:token" element={<PublicCheckout />} />
          <Route path="/bilgi-formu/:branchId/:token" element={<PublicOnboarding />} />
          {!user ? (
            <Route path="*" element={<Navigate to="/login" />} />
          ) : showSetup ? (
            <Route path="*" element={<SetupWizard onComplete={handleSetupComplete} />} />
          ) : !modulesLoaded ? (
            // industryModules henuz gelmedi - asagidaki module-gated Route'lar (calendar,
            // messages, automations vs.) HENUZ YOK, bu haliyle render edilirse mevcut URL
            // hicbirine eslesmeyip catch-all'a dusup "/" a atardi. Modul listesi gelene
            // kadar sayfayi/URL'yi degistirmeden bekle.
            <Route path="*" element={<div className="loading">Yükleniyor...</div>} />
          ) : (
            <Route path="/*" element={
              <div className="layout">
                {isModuleEnabled('announcements') && <AnnouncementPopup />}
                <Sidebar industry={industry} industryModules={industryModules} />
                <main className="main-content">
                  {subscriptionStatus === 'suspended' && (
                    <div style={{
                      background: 'linear-gradient(135deg, #DC2626, #B91C1C)',
                      color: '#fff', padding: '10px 20px', fontSize: '13.5px', fontWeight: 600,
                      textAlign: 'center', position: 'sticky', top: 0, zIndex: 500
                    }}>
                      ⚠️ Ödemeniz gecikti, hesabınız kısıtlı modda — yeni randevu/müşteri/satış gibi kayıtlar oluşturulamıyor. Lütfen bizimle iletişime geçin.
                    </div>
                  )}
                  <Routes>
                    <Route path="/" element={<Dashboard industry={industry} />} />
                    <Route path="/egitim" element={<Training />} />
                    {isModuleEnabled('calendar') && <Route path="/calendar" element={<Calendar industry={industry} />} />}
                    {isModuleEnabled('customers') && <Route path="/customers" element={<Customers />} />}
                    {isModuleEnabled('customers') && <Route path="/customers/:id" element={<CustomerDetail />} />}
                    {isModuleEnabled('opportunities') && <Route path="/opportunities" element={<Opportunities />} />}
                    {isModuleEnabled('staff') && <Route path="/staff" element={<Staff />} />}
                    {isModuleEnabled('payroll') && <Route path="/payroll" element={<Payroll />} />}
                    {isModuleEnabled('services') && <Route path="/services" element={<Services />} />}
                    {isModuleEnabled('measurements') && <Route path="/measurements" element={<Measurements />} />}
                    {isModuleEnabled('fitting') && <Route path="/fitting" element={<FittingSchedule />} />}
                    {isModuleEnabled('rental') && <Route path="/rental" element={<RentalContracts />} />}
                    {isModuleEnabled('surveys') && <Route path="/surveys" element={<Surveys />} />}
                    {isModuleEnabled('quotes') && <Route path="/quotes" element={<Quotes />} />}
                    {isModuleEnabled('production') && <Route path="/production" element={<Production />} />}
                    {isModuleEnabled('messages') && <Route path="/messages" element={<Messages />} />}
                    {isModuleEnabled('campaigns') && <Route path="/campaigns" element={<Campaigns />} />}
                    {isModuleEnabled('reports') && <Route path="/reports" element={<Reports />} />}
                    {isModuleEnabled('calls') && <Route path="/calls" element={<Calls />} />}
                    {isModuleEnabled('announcements') && <Route path="/announcements" element={<Announcements />} />}
                    {isModuleEnabled('callbacks') && <Route path="/callbacks" element={<Callbacks />} />}
                    {/* wedding_hall routes */}
                    {isModuleEnabled('reservations') && <Route path="/reservations" element={<WeddingReservations />} />}
                    {isModuleEnabled('packages') && <Route path="/packages" element={<WeddingPackages />} />}
                    {isModuleEnabled('eventChecklist') && <Route path="/event-checklist" element={<WeddingEventChecklist />} />}
                    {isModuleEnabled('weddingVendors') && <Route path="/wedding-vendors" element={<WeddingVendors />} />}
                    {/* health_tourism routes */}
                    {isModuleEnabled('patientTracking') && <Route path="/patient-tracking" element={<PatientTracking />} />}
                    {/* hotel routes */}
                    {isModuleEnabled('hotelReservations') && <Route path="/hotel-reservations" element={<HotelReservations />} />}
                    {/* dental_clinic routes */}
                    {isModuleEnabled('dentalTreatments') && <Route path="/dental-treatments" element={<DentalTreatments />} />}
                     {/* clinic routes */}
                    {isModuleEnabled('departments') && <Route path="/departments" element={<Departments />} />}
                    {isModuleEnabled('patientSatisfaction') && <Route path="/patient-satisfaction" element={<PatientSatisfaction />} />}
                    {/* autoReminders shared (dental + clinic) */}
                    {isModuleEnabled('autoReminders') && <Route path="/auto-reminders" element={<AutoReminders />} />}
                    {/* ecommerce routes */}
                    {isModuleEnabled('ecommerceOrders') && <Route path="/ecommerce-orders" element={<EcommerceOrders />} />}
                    {isModuleEnabled('returnManagement') && <Route path="/return-management" element={<ReturnManagement />} />}
                    {isModuleEnabled('pos') && <Route path="/pos" element={<POS />} />}
                    {isModuleEnabled('products') && <Route path="/products" element={<Products industry={industry} />} />}
                    {isModuleEnabled('settings') && <Route path="/settings" element={<Settings />} />}
                    {isModuleEnabled('metaAds') && <Route path="/meta-ads" element={<MetaAds />} />}
                    {isModuleEnabled('integrations') && <Route path="/integrations" element={<Integrations />} />}
                    {isModuleEnabled('preConsultations') && <Route path="/pre-consultations" element={<PreConsultations />} />}
                     {isModuleEnabled('kanban') && <Route path="/kanban" element={<LeadsKanban />} />}
                     {isModuleEnabled('finance') && <Route path="/finance" element={<FinanceDashboard />} />}
                     {isModuleEnabled('inventoryAlerts') && <Route path="/inventory-alerts" element={<FinanceDashboard defaultTab="alerts" />} />}
                     {isModuleEnabled('automations') && <Route path="/automations" element={<Automations />} />}
                     {isModuleEnabled('notifications') && <Route path="/notifications" element={<Notifications />} />}
                    


                    <Route path="*" element={<Navigate to="/" />} />
                  </Routes>
                </main>
              </div>
            } />
          )}
        </Routes>
        <ToastContainer position="bottom-right" theme="dark" autoClose={3500} />
      </BrowserRouter>
    </AuthContext.Provider>
  );
}

export default App;
